// client/src/components/pages/Geofencing/VehicleDetailPanel.tsx (converted from .js, ST-00244)
// LexiStar vehicle detail panel. Slides in from the right on desktop and up as
// a bottom sheet on mobile when a vehicle is selected on the live map. Shows
// identity, live movement, zone membership, and a decoded-telemetry grid, plus
// jump-to-trips / recenter actions. Fed the rich /fleet record from the page.
// ST-00253 / ST-00244: the status pill takes the vehicle's colour from the shared asset status
// scheme (`status`: the LexiStar status board's key when it has one, else its motion).
import { useEffect, useRef, useState, type ComponentType } from 'react';
import {
  FaTimes,
  FaRoute,
  FaCrosshairs,
  FaKey,
  FaBolt,
  FaCarBattery,
  FaSignal,
  FaSatelliteDish,
  FaRoad,
  FaLocationArrow,
} from 'react-icons/fa';
import { ZoneTypeBadge as ZoneTypeBadgeJs } from './zoneTypes';
import { StatusWithMarks, ClassBadge, liveStatusOf, type LiveStatus } from './vehicleStatus';
import { formatMiningDateTime as formatMiningDateTimeJs } from '../../../utils/dateUtils';
import type { FleetVehicle } from '../../../types/clientPages/geofencing';

const ZoneTypeBadge = ZoneTypeBadgeJs as unknown as ComponentType<{ type?: string | null }>;
const formatMiningDateTime = formatMiningDateTimeJs as unknown as (
  ts: string | null | undefined
) => string;

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
const headingLabel = (deg: number | null | undefined): string | null =>
  deg == null ? null : (COMPASS[Math.round(deg / 45) % 8] ?? null);

// mV → "12.8 V" (1 decimal), or em-dash for null/undefined.
const formatVolts = (mv: number | null | undefined): string =>
  mv == null ? '—' : `${(mv / 1000).toFixed(1)} V`;

/** A zone the vehicle is in, as /fleet sends it (a name, or name + zone type). */
type CurrentZone = string | { name: string; zoneType?: string | null };

// ─── Telemetry cell ─────────────────────────────────────────────────────────
interface TelemetryCellProps {
  icon: ComponentType<{ size?: number; className?: string }>;
  label: string;
  value: string | number | null | undefined;
  tone?: string;
}

function TelemetryCell({ icon: Icon, label, value, tone = 'text-slate-800' }: TelemetryCellProps) {
  const empty = value === '—' || value == null;
  return (
    <div className="rounded-lg border border-slate-200 bg-surface p-2.5">
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-400 uppercase tracking-wide">
        <Icon size={11} className="flex-shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      <p className={`mt-1 text-sm font-semibold tabular-nums ${empty ? 'text-slate-300' : tone}`}>
        {value == null ? '—' : value}
      </p>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────
export interface VehicleDetailPanelProps {
  vehicle: FleetVehicle | null;
  onClose: () => void;
  onViewTrips?: (regNo: string) => void;
  onCenter?: (regNo: string) => void;
  /** The vehicle's status (colour key + words); default: its motion only. */
  status?: LiveStatus | null;
}

export default function VehicleDetailPanel({
  vehicle,
  onClose,
  onViewTrips,
  onCenter,
  status,
}: VehicleDetailPanelProps) {
  const [shown, setShown] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const open = vehicle != null;

  // Entrance transition. The panel stays mounted while closed (it renders nothing without a
  // vehicle), so the slide-in follows the open state, not the mount: every open slides in again.
  // ST-00285: keyed to the mount, a second open stayed off-screen and, on a phone, left the
  // transparent backdrop over the map, so the next tap only closed it again.
  useEffect(() => {
    if (!open) {
      setShown(false);
      return undefined;
    }
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    []
  );

  if (!vehicle) return null;

  // Exit: slide out, then tell the page (after the 200 ms transition).
  const requestClose = () => {
    setShown(false);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      onClose();
    }, 200);
  };

  const t: Partial<FleetVehicle['telemetry']> = vehicle.telemetry || {};
  const hdg = vehicle.heading;
  const dir = headingLabel(hdg);
  const zones = ((vehicle.currentZones || []) as CurrentZone[]).map((z) =>
    typeof z === 'string' ? { name: z, zoneType: null } : z
  );
  const liveStatus = status ?? liveStatusOf(vehicle.status);

  return (
    <>
      {/* Backdrop — mobile only so the desktop map stays usable beside the drawer */}
      <div
        className={`fixed inset-0 z-40 bg-slate-900/40 dark:bg-black/40 lg:hidden transition-opacity duration-200 ${shown ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        data-testid="vehicle-detail-backdrop"
        onClick={requestClose}
        aria-hidden
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Vehicle ${vehicle.regNo} details`}
        className={`fixed z-50 bg-slate-50 shadow-2xl flex flex-col transition-transform duration-200 ease-out
          inset-x-0 bottom-0 max-h-[85vh] rounded-t-2xl
          lg:inset-y-0 lg:right-0 lg:left-auto lg:bottom-auto lg:h-full lg:w-full lg:max-w-md lg:rounded-none lg:border-l lg:border-slate-200
          ${shown ? 'translate-y-0 lg:translate-x-0' : 'translate-y-full lg:translate-y-0 lg:translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-4 border-b border-slate-200 bg-surface lg:rounded-none rounded-t-2xl flex-shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-slate-800 truncate">{vehicle.regNo}</h2>
              <ClassBadge vehicleClass={vehicle.vehicleClass} />
              <StatusWithMarks status={liveStatus} />
            </div>
            {(vehicle.label || vehicle.model) && (
              <p className="text-sm text-slate-500 mt-0.5 truncate">
                {vehicle.label}
                {vehicle.label && vehicle.model ? ' · ' : ''}
                {vehicle.model}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="p-2 -m-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg flex-shrink-0"
            aria-label="Close details"
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
          {/* Movement summary */}
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-lg border border-slate-200 bg-surface p-3 text-center">
              <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wide">
                Speed
              </p>
              <p className="mt-1 text-xl font-bold text-slate-800 tabular-nums">
                {Math.round(vehicle.speed || 0)}
              </p>
              <p className="text-[10px] text-slate-400">km/h</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-surface p-3 text-center">
              <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wide">
                Heading
              </p>
              {hdg == null ? (
                <p className="mt-1 text-xl font-bold text-slate-300 tabular-nums">—</p>
              ) : (
                <div className="mt-1 flex items-center justify-center gap-1.5">
                  <FaLocationArrow
                    size={13}
                    className="text-teal-500"
                    style={{ transform: `rotate(${Math.round(hdg) - 45}deg)` }}
                  />
                  <span className="text-xl font-bold text-slate-800 tabular-nums">
                    {Math.round(hdg)}°
                  </span>
                </div>
              )}
              <p className="text-[10px] text-slate-400">{dir || ' '}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-surface p-3 text-center">
              <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wide">
                Today
              </p>
              <p className="mt-1 text-xl font-bold text-slate-800 tabular-nums">
                {vehicle.todayKm ?? 0}
              </p>
              <p className="text-[10px] text-slate-400">km</p>
            </div>
          </div>

          {/* Zone membership */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Current zone
            </p>
            {zones.length > 0 ? (
              <div className="space-y-1.5">
                {zones.map((z, i) => (
                  <div key={`${z.name}-${i}`} className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-700">{z.name}</span>
                    <ZoneTypeBadge type={z.zoneType} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">Not in any zone</p>
            )}
          </div>

          {/* Telemetry grid */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Telemetry
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <TelemetryCell
                icon={FaKey}
                label="Ignition"
                value={t.ignition == null ? '—' : t.ignition ? 'On' : 'Off'}
                tone={t.ignition ? 'text-emerald-600' : 'text-slate-800'}
              />
              <TelemetryCell
                icon={FaBolt}
                label="External"
                value={formatVolts(t.externalVoltageMv)}
              />
              <TelemetryCell
                icon={FaCarBattery}
                label="Battery"
                value={formatVolts(t.batteryVoltageMv)}
              />
              <TelemetryCell
                icon={FaSignal}
                label="GSM"
                value={t.gsmSignal == null ? '—' : t.gsmSignal}
              />
              <TelemetryCell
                icon={FaSatelliteDish}
                label="Satellites"
                value={t.satellites == null ? '—' : t.satellites}
              />
              <TelemetryCell
                icon={FaRoad}
                label="Odometer"
                value={t.odometerKm == null ? '—' : `${t.odometerKm.toLocaleString()} km`}
              />
            </div>
          </div>

          {/* Last seen */}
          <div className="text-xs text-slate-400">
            Last reported{' '}
            <span className="font-medium text-slate-500">
              {formatMiningDateTime(vehicle.ts) || '—'}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex-shrink-0 border-t border-slate-200 bg-surface p-3 flex gap-2">
          <button
            type="button"
            onClick={() => onViewTrips?.(vehicle.regNo)}
            className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <FaRoute size={13} className="text-teal-500" />
            View trips
          </button>
          <button
            type="button"
            onClick={() => onCenter?.(vehicle.regNo)}
            className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors"
          >
            <FaCrosshairs size={13} />
            Center on map
          </button>
        </div>
      </div>
    </>
  );
}
